import {
	ABOUT_TWELVE_MONTH_SIM_ONLY,
	TWELVE_MONTH_SIM_ONLY_HEADER,
} from "@/data/consts";
import { Article as ArticleType, HandsetDeal } from "@/types/index";
import * as React from "react";
import { useRouter } from "next/router";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getOne } from "@/api/contentful";
import Article from "@/components/Article";
import { GetStaticProps, NextPage } from "next";
import { createDealClick } from "@/api/tracking";
import PageSEO from "@/components/Shared/PageSEO";
import { UtmContext } from "@/context/utmContext";
import { KeywordContext } from "@/context/keyword";
import Filter from "@/components/Comparison/Filter";
import Sorter from "@/components/Comparison/Sorter";
import Loading from "@/components/Comparison/Loading";
import LoadMore from "@/components/Comparison/LoadMore";
import useMobileComparison from "@/hooks/useMobileComparison";
import Layout from "@/components/Comparison/Layouts/MobileLayout";
import HeaderContent from "@/components/Comparison/HeaderContent";
import DealsNotFound from "@/components/Comparison/DealsNotFound";
import { HeaderContent as HeaderContentType } from "@/types/header";
import SIMOnlyDeal from "@/components/Comparison/Deals/SIMOnlyDeal";
import MobileFilter from "@/components/Comparison/Filter/MobileFilter";
import Head from "next/head";

const Breadcrumbs = [
	{
		title: "Home",
		href: "/",
		active: false,
	},
	{
		title: "Mobile",
		href: "/mobile",
		active: false,
	},
	{
		title: "SIM Only Deals",
		href: "/mobile/sim-only",
		active: false,
	},
	{
		title: "12 Month SIM Only Deals",
		href: "/mobile/sim-only/12-month",
		active: true,
	},
];

const TwelveMonthSimOnlyDeals: NextPage<{
	article: ArticleType;
	headerContent: HeaderContentType;
}> = ({ headerContent, article }) => {
	const router = useRouter();
	const { utmContent, utmCampaign, utmMedium, utmSource, utmTerm } =
		React.useContext(UtmContext);
	const { sclid, gclid, fbclid, msclkid, tbclid, obclid } =
		React.useContext(KeywordContext);

	const mobileComparison = useMobileComparison(
		undefined,
		undefined,
		undefined,
		true,
		undefined,
		12
	);

	const incrementViewableDeals = () => {
		mobileComparison.setPage(mobileComparison.page + 1);
	};

	const deals = mobileComparison.products.map(function (deal, index) {
		const onDealClick = async (product: HandsetDeal) => {
			createDealClick({
				type: "sim-only",
				click_reference:
					sclid ?? gclid ?? fbclid ?? msclkid ?? tbclid ?? obclid ?? utmContent,
				utm_campaign: utmCampaign,
				utm_content: utmContent,
				utm_source: utmSource,
				utm_medium: utmMedium,
				utm_term: utmTerm,
				product: product.network,
				provider: product.network,
				metadata: {
					dealPosition: index + 1,
					page: router.isReady ? router.asPath : "",
					monthlyCost: product.monthly_price,
					handsetPrice: product.handset_price,
					upfrontCost: product.upfront_price,
					contractLength: product.contract_length,
					dataAllowance: product.data_allowance,
					minutesAllowance: product.minutes_allowance,
					textsAllowance: product.texts_allowance,
				},
			});
		};

		return (
			<div className="col-12 text-start" key={index}>
				<SIMOnlyDeal
					data={deal}
					urlReference={
						sclid ??
						gclid ??
						fbclid ??
						msclkid ??
						tbclid ??
						obclid ??
						utmContent
					}
					onDealClick={onDealClick}
				/>
			</div>
		);
	});

	const filterProps = [
		{
			title: "Monthly Cost",
			filters: mobileComparison.monthlyFilters,
			toggleFilter: mobileComparison.toggleMonthlyFilter,
		},
		{
			title: "Contract Length",
			filters: mobileComparison.contractFilters,
			toggleFilter: mobileComparison.toggleContractFilter,
		},
		{
			title: "Data",
			filters: mobileComparison.dataFilters,
			toggleFilter: mobileComparison.toggleDataFilter,
		},
		{
			title: "Network",
			filters: mobileComparison.networkFilters,
			toggleFilter: mobileComparison.toggleNetworkFilter,
		},
		{
			title: "Upfront Cost",
			filters: mobileComparison.upFrontCostFilters,
			toggleFilter: mobileComparison.toggleUpFrontCostFilter,
		},
		{
			title: "Minutes",
			filters: mobileComparison.minutesFilters,
			toggleFilter: mobileComparison.toggleMinutesFilter,
		},
	];

	return (
		<div className="h-100 d-flex flex-column">
			<Head>
				<link
					rel="canonical"
					href="https://www.savely.co.uk/mobile/sim-only/12-month"
				/>
			</Head>
			<PageSEO
				title={
					"Compare 12 Month SIM Only Mobile Deals: Get Long-Term Value and Savings | Consumer Choices"
				}
				description={
					"Compare 12 month SIM only mobile deals and save by committing to a longer contract! See a wide range of data, minutes and text options and pick the right one for you. "
				}
				keywords={
					"12 Month SIM Only Mobile Deals, cheap SIM only, SIM only savings "
				}
			/>
			<Header
				left={<HeaderContent data={headerContent} />}
				right={<></>}
				breadcrumbs={Breadcrumbs}
			/>
			<div id="deals">
				<Layout
					desktopFilters={<Filter filterProps={filterProps} />}
					mobileFilters={<MobileFilter filterProps={filterProps} />}
					comparisonTable={
						mobileComparison.loading ? (
							<Loading />
						) : deals.length > 0 ? (
							deals
						) : (
							<DealsNotFound clearFilters={mobileComparison.clearFilters} />
						)
					}
					loadMore={
						deals.length > 0 &&
						mobileComparison.hasNextPage &&
						!mobileComparison.loading ? (
							<LoadMore onClick={incrementViewableDeals} />
						) : (
							<></>
						)
					}
					sorter={
						<Sorter
							defaultSort={"savely_recommended"}
							sortPropsVariant={"mobile"}
							setSorter={mobileComparison.setSorter}
						/>
					}
				/>
			</div>
			<Article article={article} showAuthor={false} showSideBar={false} />
			<Footer />
		</div>
	);
};

export const getStaticProps: GetStaticProps = async () => {
	const headerContent = await getOne(TWELVE_MONTH_SIM_ONLY_HEADER);
	const article = await getOne(ABOUT_TWELVE_MONTH_SIM_ONLY);

	return {
		props: {
			headerContent,
			article,
		},
		revalidate: Number(process.env.REVALIDATE_CONTENTFUL),
	};
};

export default TwelveMonthSimOnlyDeals;
